import { DeepPartial } from 'react-hook-form';
import { DirectSignupFilters, ResumedLeadState } from '../context';
import { Contact } from 'domains/contact/types';
import { EvaluationScheduleFormType } from '../schemas/evaluationScheduleSchema';
import { TherapyTargetValue } from '../misc/constants';
import { ContactRelationship } from 'domains/client';
import { isPCPRequired } from '../businessLogic/isPCPRequired';
import { EligibilityCheckResult } from '../SyntaxComps/EligibilityCard';
import { InsurancePayment } from '../schemas/filtersSchema';
import { get } from 'lodash';
import { EvaluationFormType, getInsuranceSchemaType } from '../schemas/evaluationSchema';

const getDefaultRelationshipToClient = (
  target: TherapyTargetValue,
): EvaluationFormType['contactInfo']['relationshipToClient'] | undefined => {
  switch (target) {
    case 'adult':
      return ContactRelationship.Client;
    default:
      return undefined;
  }
};

/** Merges various sources of information and calculates the default values for the evaluation form */
export const getEvaluationFormDefaultValues = (data: {
  evaluationSchedule: EvaluationScheduleFormType;
  filters: DirectSignupFilters;
  incompleteEvalForm?: DeepPartial<EvaluationFormType>;
  existingContactData?: Contact;
  resumedLeadState?: ResumedLeadState;
}): DeepPartial<EvaluationFormType> => {
  const { resumedLeadState, evaluationSchedule, filters, incompleteEvalForm, existingContactData } = data;
  const { paymentMethod } = filters;

  return {
    leadId: resumedLeadState?.leadID,
    evaluationSchedule: evaluationSchedule,
    clientType: filters.therapyTarget,
    concerns: {
      clinicalAreas: filters.clinicalAreas,
      clinicalConcernKeys: filters.clinicalConcernKeys,
    },
    clientInfo: {
      language: filters.language,
      stateCode: filters.stateCode,
      firstName: incompleteEvalForm?.clientInfo?.firstName,
      lastName: incompleteEvalForm?.clientInfo?.lastName,
      dateOfBirth: incompleteEvalForm?.clientInfo?.dateOfBirth,
    },
    contactInfo: {
      relationshipToClient:
        incompleteEvalForm?.contactInfo?.relationshipToClient ?? getDefaultRelationshipToClient(filters.therapyTarget),

      ...(existingContactData
        ? {
            firstName: existingContactData.contactFirstName,
            lastName: existingContactData.contactLastName,
            email: existingContactData.email,
            phone: existingContactData.phone?.mobile ?? '',
            contactID: existingContactData.contactID,
          }
        : {
            firstName: incompleteEvalForm?.contactInfo?.firstName,
            lastName: incompleteEvalForm?.contactInfo?.lastName,
            email: incompleteEvalForm?.contactInfo?.email,
            phone: incompleteEvalForm?.contactInfo?.phone,
            contactID: undefined,
          }),
    },
    primaryPhysician: {
      ...incompleteEvalForm?.primaryPhysician,
      physicianOrderRequired: isPCPRequired(filters.paymentMethod),
    },
    billingInfo: {
      ...(paymentMethod.type === 'insurance' && {
        type: getInsuranceSchemaType(paymentMethod.insuranceData.insuranceType),
        insuranceData: (filters.paymentMethod as InsurancePayment).insuranceData,

        eligibilityCheckResult: resumedLeadState?.shouldSkipEligibility
          ? EligibilityCheckResult.IN_NETWORK
          : (filters.paymentMethod as InsurancePayment).insuranceData.networkStatus === 'Out-of-network'
          ? EligibilityCheckResult.OUT_OF_NETWORK
          : undefined,
        subscriber:
          incompleteEvalForm?.billingInfo?.type === 'insurance-commercial'
            ? incompleteEvalForm?.billingInfo.subscriber
            : undefined,
        memberId: get(incompleteEvalForm, 'billingInfo.memberId'),
      }),
      ...(paymentMethod.type === 'privatePay' && {
        type: 'privatePay',
      }),
    } as EvaluationFormType['billingInfo'],
  };
};
